import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, LogOut } from "@yk/ui/icons";
import { useAuthStore } from "@yk/frontend-core";
import { authApi } from "../lib/api.js";

/** Humanize a role code, e.g. "dept_supervisor" → "DEPT SUPERVISOR". */
function roleCaption(roles: readonly string[] | undefined): string {
  const first = roles?.[0];
  if (!first) return "WORKSPACE";
  return first.replace(/_/g, " ").toUpperCase();
}

/** Contextual workspace title shown at the start of the topbar. Role-based and
 *  brand-neutral (never surfaces the org slug). */
export function workspaceTitle(roles: readonly string[] | undefined): string {
  const r = roles?.[0] ?? "";
  if (r === "super_admin") return "Platform Admin";
  if (r === "dept_supervisor" || r === "senior_supervisor")
    return "Supervisor View";
  return "Organisation Admin";
}

/** Sidebar brand: gradient logo tile + product name + role caption. */
export function SidebarBrand({
  roles,
}: {
  roles?: readonly string[] | undefined;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-indigo-700 text-primary-fg shadow-sm">
        <Wrench className="size-5" aria-hidden="true" />
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-sm font-bold text-fg">Facility Management</span>
        <span className="truncate text-[10px] font-semibold uppercase tracking-wider text-muted">
          {roleCaption(roles)}
        </span>
      </span>
    </div>
  );
}

/** Sidebar footer: avatar + name/email + Sign out. */
export function SidebarUser(): React.JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  if (!user) return null;

  const name =
    [user.firstNameEn, user.lastNameEn].filter(Boolean).join(" ") || user.email;
  const initials =
    (user.firstNameEn?.[0] ?? "") + (user.lastNameEn?.[0] ?? "") ||
    user.email[0]?.toUpperCase() ||
    "?";

  async function handleSignOut(): Promise<void> {
    if (signingOut) return;
    setSigningOut(true);
    try {
      try {
        await authApi.signOut();
      } catch (err) {
        console.error("sign-out failed; clearing locally", err);
      }
      clear();
      navigate("/login");
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
          {initials.toUpperCase()}
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-sm font-semibold text-fg">{name}</span>
          <span className="truncate text-xs text-muted">{user.email}</span>
        </span>
      </div>
      <button
        type="button"
        onClick={() => void handleSignOut()}
        disabled={signingOut}
        data-testid="sign-out-button"
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-muted transition-colors hover:bg-muted/10 hover:text-fg disabled:opacity-50"
      >
        <LogOut className="size-4" aria-hidden="true" />
        Sign out
      </button>
    </div>
  );
}
